import { Card } from "@mui/material";
import axios from "axios";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { server } from "../../server";
import { RiSearchLine } from "react-icons/ri";
import { useNavigate } from "react-router-dom";

export default function ShopList() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [shops, setShops] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");

  const getAllShops = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${server}/shop/all`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("bvmToken")}`,
        },
        params: {
          pageNo: 1,
          pageSize: 10000,
        }
      });
      setShops(data?.data || []);
      setLoading(false);
    } catch (e) {
      setLoading(false);
      toast.error(e?.response?.data?.message || e?.message || "Failed to fetch shops");
    }
  };

  useEffect(() => {
    getAllShops();
  }, []);

  const filteredShops = shops.filter(
    (shop) =>
      shop.shopName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      shop.shopCode?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="flex justify-center w-full p-4 bg-slate-50 min-h-screen">
      <div className="w-full max-w-6xl">
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-6 gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-800 tracking-tight">
              All Shops
            </h1>
            <p className="text-slate-500 mt-1">
              Manage and view all registered shops
            </p>
          </div>

          <div className="relative w-full md:w-72">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <RiSearchLine className="text-slate-400" />
            </div>
            <input
              type="text"
              placeholder="Search shops..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 w-full border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-slate-700 placeholder-slate-400 shadow-sm transition-all"
            />
          </div>
        </div>

        <Card
          sx={{
            borderRadius: 3,
            boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
            overflow: "hidden",
          }}
          className="bg-white"
        >
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left text-slate-600">
              <thead className="text-xs text-slate-700 uppercase bg-slate-100 border-b border-slate-200">
                <tr>
                  <th className="px-6 py-4 font-bold">S.No.</th>
                  <th className="px-6 py-4 font-bold">Shop Name</th>
                  <th className="px-6 py-4 font-bold">Shop Code</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="3" className="px-6 py-12 text-center">
                      <div className="flex flex-col items-center justify-center">
                        <svg className="animate-spin h-8 w-8 text-blue-500 mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <p className="text-slate-500 font-medium">Loading shops...</p>
                      </div>
                    </td>
                  </tr>
                ) : filteredShops.length === 0 ? (
                  <tr>
                    <td colSpan="3" className="px-6 py-12 text-center">
                      <div className="flex flex-col items-center justify-center">
                        <div className="bg-slate-100 p-4 rounded-full mb-3">
                          <RiSearchLine className="text-2xl text-slate-400" />
                        </div>
                        <p className="text-slate-800 font-medium">No shops found</p>
                        <p className="text-slate-500 text-sm mt-1">
                          {searchTerm ? `No matches for "${searchTerm}"` : "Get started by adding a new shop"}
                        </p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  filteredShops.map((shop, index) => (
                    <tr
                      key={shop._id || shop.id || shop.shopCode || index}
                      onClick={() => {
                        const shopId = shop._id || shop.id || shop.shopCode;
                        console.log("Navigating to shop:", shopId, shop);
                        navigate(`/my/shopDetails`, { state: { shopId: shopId } });
                      }}
                      className="bg-white border-b border-slate-100 hover:bg-blue-50 transition-colors duration-150 cursor-pointer"
                    >
                      <td className="px-6 py-4 font-medium text-slate-900">
                        {index + 1}
                      </td>
                      <td className="px-6 py-4 text-slate-700 font-medium">
                        {shop.shopName}
                      </td>
                      <td className="px-6 py-4 text-slate-700">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 border border-blue-200">
                          {shop.shopCode}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <div className="px-6 py-4 border-t border-slate-100 bg-slate-50 flex justify-between items-center">
            <span className="text-xs text-slate-500">
              Showing {filteredShops.length} of {shops.length} entries
            </span>
            <div className="flex gap-2">
              <button disabled className="px-3 py-1 text-xs font-medium text-slate-400 bg-white border border-slate-200 rounded hover:bg-slate-50 disabled:opacity-50 cursor-not-allowed">
                Previous
              </button>
              <button disabled className="px-3 py-1 text-xs font-medium text-slate-400 bg-white border border-slate-200 rounded hover:bg-slate-50 disabled:opacity-50 cursor-not-allowed">
                Next
              </button>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}
